import { useState } from "react";
import { useGetOne, useGetList, useUpdate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft, Store, Mail, Phone, MapPin, Calendar,
  ClipboardList, Plus, DollarSign,
} from "lucide-react";
import { VendorStatusBadge } from "./VendorStatusBadge";
import { CreatePODialog } from "./CreatePODialog";

interface Vendor {
  id: string;
  name: string;
  contact_name: string;
  email: string | null;
  phone: string | null;
  address: string;
  payment_terms: string;
  credit_limit: number | null;
  status: string;
  portal_token: string;
  total_ordered: number;
  total_paid: number;
  balance_owed: number;
  notes: string;
  created_at: string;
}

interface PO {
  id: string;
  po_number: string;
  job_id: string | null;
  status: string;
  description: string;
  total: number;
  ordered_at: string | null;
  received_at: string | null;
  paid_at: string | null;
  created_at: string;
}

interface VendorDetailProps {
  vendorId: string;
  onBack: () => void;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const TERMS_LABELS: Record<string, string> = {
  cod: "COD", net_15: "Net 15", net_30: "Net 30", net_45: "Net 45", net_60: "Net 60",
};

const PO_STATUS_CONFIG: Record<string, { color: string; bg: string }> = {
  draft: { color: "#6b7280", bg: "#f3f4f6" },
  submitted: { color: "#2563eb", bg: "#dbeafe" },
  received: { color: "#d97706", bg: "#fef3c7" },
  paid: { color: "#16a34a", bg: "#dcfce7" },
  cancelled: { color: "#dc2626", bg: "#fee2e2" },
};

export function VendorDetail({ vendorId, onBack }: VendorDetailProps) {
  const { data: vendor, isPending } = useGetOne<Vendor>("vendors", { id: vendorId });
  const { data: pos } = useGetList<PO>("purchase_orders", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
    filter: { vendor_id: vendorId },
  });
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [poDialogOpen, setPODialogOpen] = useState(false);

  const handlePOStatus = async (po: PO, newStatus: string) => {
    const updates: Record<string, unknown> = { status: newStatus };
    if (newStatus === "submitted") updates.ordered_at = new Date().toISOString();
    if (newStatus === "received") updates.received_at = new Date().toISOString();
    if (newStatus === "paid") updates.paid_at = new Date().toISOString();

    await update("purchase_orders", { id: po.id, data: updates }, { returnPromise: true });
    notify(`PO ${po.po_number} → ${newStatus}`, { type: "success" });
    refresh();
  };

  if (isPending || !vendor) {
    return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded hover:bg-gray-100">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4 text-muted-foreground" />
            <VendorStatusBadge status={vendor.status} />
            <span className="text-xs text-muted-foreground bg-gray-100 px-1.5 py-0.5 rounded">
              {TERMS_LABELS[vendor.payment_terms] || vendor.payment_terms}
            </span>
          </div>
          <h3 className="text-lg font-semibold truncate mt-0.5">{vendor.name}</h3>
          {vendor.contact_name && (
            <p className="text-xs text-muted-foreground">{vendor.contact_name}</p>
          )}
        </div>
      </div>

      {/* Contact info */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            {vendor.phone && (
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {vendor.phone}</span>
            )}
            {vendor.email && (
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {vendor.email}</span>
            )}
            {vendor.address && (
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {vendor.address}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Balance card */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold tabular-nums text-blue-600">
              {fmt(Number(vendor.total_ordered))}
            </p>
            <p className="text-xs text-muted-foreground">Total Ordered</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold tabular-nums text-green-600">
              {fmt(Number(vendor.total_paid))}
            </p>
            <p className="text-xs text-muted-foreground">Total Paid</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold tabular-nums" style={{
              color: Number(vendor.balance_owed) > 0 ? "#d97706" : "#16a34a",
            }}>
              {fmt(Number(vendor.balance_owed))}
            </p>
            <p className="text-xs text-muted-foreground">Balance Owed</p>
          </CardContent>
        </Card>
      </div>

      {/* Purchase orders */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold flex items-center gap-1.5">
            <ClipboardList className="w-4 h-4" />
            Purchase Orders ({pos?.length || 0})
          </h4>
          <button onClick={() => setPODialogOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700">
            <Plus className="w-3 h-3" /> New PO
          </button>
        </div>

        {(!pos || pos.length === 0) && (
          <p className="text-xs text-muted-foreground">No purchase orders yet.</p>
        )}

        {pos && pos.length > 0 && (
          <div className="space-y-1.5">
            {pos.map((po) => {
              const sc = PO_STATUS_CONFIG[po.status] || PO_STATUS_CONFIG.draft;
              return (
                <Card key={po.id} className="shadow-none">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-mono text-muted-foreground">{po.po_number}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded font-medium"
                            style={{ color: sc.color, backgroundColor: sc.bg }}>
                            {po.status}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate">{po.description || "Purchase"}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(po.created_at)}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-sm font-bold tabular-nums">{fmt(Number(po.total))}</p>
                        <div className="flex gap-1 mt-1 justify-end">
                          {po.status === "draft" && (
                            <button onClick={() => handlePOStatus(po, "submitted")}
                              className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100">
                              Submit
                            </button>
                          )}
                          {po.status === "submitted" && (
                            <button onClick={() => handlePOStatus(po, "received")}
                              className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-600 hover:bg-amber-100">
                              Received
                            </button>
                          )}
                          {(po.status === "submitted" || po.status === "received") && (
                            <button onClick={() => handlePOStatus(po, "paid")}
                              className="text-xs px-2 py-0.5 rounded bg-green-50 text-green-600 hover:bg-green-100">
                              Paid
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <CreatePODialog open={poDialogOpen} onClose={() => setPODialogOpen(false)} vendorId={vendorId} />
    </div>
  );
}
